import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, UserPlus, Briefcase, Kanban, FileText, Shield,
  CalendarClock, CheckSquare, BarChart3, FileSpreadsheet, Clock, PhoneCall,
  LogOut, ChevronRight, Building2,
} from "lucide-react";
import { useAuth, ROLE_LABEL, canRole } from "../lib/auth";
import Logo from "./Logo";
import GlobalSearch from "./GlobalSearch";

// Menu shown for full CRM roles (branch_manager, admin, super_admin, telecaller)
const NAV_FULL = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true, min: "telecaller" },
  { to: "/admin/leads", label: "Leads", icon: UserPlus, min: "telecaller" },
  { to: "/admin/telecaller", label: "Telecaller Queue", icon: PhoneCall, min: "telecaller" },
  { to: "/admin/customers", label: "Customer 360", icon: Users, min: "telecaller" },
  { to: "/admin/loans", label: "Loan Pipeline", icon: Kanban, min: "telecaller" },
  { to: "/admin/documents", label: "Documents", icon: FileText, min: "telecaller" },
  { to: "/admin/insurance", label: "Insurance", icon: Shield, min: "telecaller" },
  { to: "/admin/followups", label: "Follow-ups", icon: CalendarClock, min: "telecaller" },
  { to: "/admin/tasks", label: "Daily Tasks", icon: CheckSquare, min: "telecaller" },
  { to: "/admin/attendance", label: "Attendance", icon: Clock, min: "telecaller" },
  { to: "/admin/employees", label: "Employees", icon: Briefcase, min: "branch_manager" },
  { to: "/admin/reports", label: "Reports", icon: BarChart3, min: "branch_manager" },
  { to: "/admin/excel", label: "Excel Import/Export", icon: FileSpreadsheet, min: "admin" },
  { to: "/admin/doc-configs", label: "Document Config", icon: FileText, min: "admin" },
];

// Executive-only restricted menu (per spec)
const NAV_EXECUTIVE = [
  { to: "/admin/leads", label: "Lead Creation", icon: UserPlus },
  { to: "/admin/attendance", label: "Attendance (GPS + Selfie)", icon: Clock },
  { to: "/admin/reports", label: "Employee Report", icon: BarChart3 },
  { to: "/admin/employees", label: "Employee Data", icon: Briefcase },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  const isExecutive = user?.role === "executive";
  const navItems = isExecutive
    ? NAV_EXECUTIVE
    : NAV_FULL.filter((n) => canRole(user, n.min));

  return (
    <div className="min-h-screen bg-[#F4F4F5] flex">
      <aside
        className={`${collapsed ? "w-16" : "w-64"} bg-[#06152D] text-white flex flex-col transition-all duration-200 sticky top-0 h-screen`}
        data-testid="admin-sidebar"
      >
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          {!collapsed ? (
            <Logo size={36} variant="light" withWordmark />
          ) : (
            <Logo size={30} variant="light" />
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="text-[#B5C4E1] hover:text-white"
            data-testid="sidebar-collapse-btn"
            aria-label="Collapse sidebar"
          >
            <ChevronRight size={16} className={collapsed ? "" : "rotate-180"} />
          </button>
        </div>

        <nav className="flex-1 py-4 overflow-y-auto">
          {navItems.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.exact}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 text-sm font-medium ${
                    isActive
                      ? "bg-[#0052FF] text-white"
                      : "text-[#B5C4E1] hover:bg-white/5 hover:text-white"
                  }`
                }
                data-testid={`sidebar-${n.to.replace(/\//g, "-")}`}
                title={n.label}
              >
                <Icon size={18} />
                {!collapsed && <span>{n.label}</span>}
              </NavLink>
            );
          })}
        </nav>

        <div className="p-4 border-t border-white/10">
          {!collapsed && (
            <div className="mb-3">
              <div className="text-xs text-[#B5C4E1]">Signed in as</div>
              <div className="text-sm font-semibold truncate" data-testid="sidebar-user-name">{user?.name}</div>
              <div className="text-xs text-[#0052FF] font-medium">{ROLE_LABEL[user?.role]}</div>
              {user?.branch && <div className="text-[10px] text-[#B5C4E1] mt-0.5">Branch: {user.branch}</div>}
            </div>
          )}
          <button
            onClick={logout}
            className="w-full flex items-center gap-2 text-[#B5C4E1] hover:text-white text-sm py-2"
            data-testid="sidebar-logout-btn"
          >
            <LogOut size={16} /> {!collapsed && "Sign out"}
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="bg-white border-b border-[#E4E4E7] px-6 py-3 flex items-center gap-4 sticky top-0 z-10">
          <div className="hidden md:flex items-center gap-2 text-sm text-[#52525B] shrink-0">
            <Building2 size={16} />
            <span>Loan Junction · {user?.branch || "Siddipet"}</span>
          </div>
          <div className="flex-1 flex justify-center">
            {!isExecutive && <GlobalSearch />}
          </div>
          <div className="text-xs text-[#52525B] hidden lg:block shrink-0">
            {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "short", year: "numeric" })}
          </div>
        </header>
        <div className="p-6">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
